import React, { Fragment, useRef } from 'react';
import generateReportUrl from "../utils/reports/generateReportUrl";
import thronThumbnailResize from "../utils/thronThumbnailResize";
import remToPixels from "../utils/remToPixels";
import PageTitle from "./PageTitle";
import styled, { css, withTheme } from "styled-components";
import { Link } from "react-router-dom";
import smallBorderStyle from "../utils/smallBorderStyle";
import collectionTypeToParentType from "../utils/collectionTypeToParentType";
import useScrollButtonsStatus from "../hooks/useScrollButtonsStatus";
import ScrollButtons from "./ScrollButtons";
import themeResponsiveProperty from "./responsive/themeResponsiveProperty";
import RenderOnTablet from "./responsive/RenderOnTablet";

function isCollectionActive(params, collectionId, collectionType) {
  return (
    params.collectionType === collectionType &&
    parseInt(params.parentId, 10) === collectionId
  );
}

export default withTheme(function ({
  collectionType,
  collections,
  match,
  ...props
}) {
  const listRef = useRef(null);
  const [leftEnabled, rightEnabled, scroll] = useScrollButtonsStatus(listRef, collections);
  const type = collectionTypeToParentType(collectionType);

  return (
    <Fragment>
      <CollectionCardList ref={listRef}>
        {collections.filter(collection => collection.type === type).map(collection => (
          <CardContainer key={collection.id}>
            <CollectionCard
              key={collection.id}
              to={generateReportUrl(collectionType, { ...match.params, parentId: collection.id })}
              cover={thronThumbnailResize(collection.cover, {
                width: remToPixels(themeResponsiveProperty(13.5, 10)(props)),
                height: remToPixels(11.5)
              })}
              isActive={isCollectionActive(match.params, collection.id, collectionType)}>
              {isCollectionActive(match.params, collection.id, collectionType) && (
                <PageTitle withTracking title={`Reports / ${collection.name}`} />
              )}
              <CollectionCardTitle>{collection.name}</CollectionCardTitle>
            </CollectionCard>
          </CardContainer>
        ))}
      </CollectionCardList>

      <RenderOnTablet>
        <ScrollButtons
          handleButtonClick={scroll}
          leftEnabled={leftEnabled}
          rightEnabled={rightEnabled}
          align="center"
        />
      </RenderOnTablet>
    </Fragment>
  );
});

const CollectionCard = styled(({ isActive, ...props }) => <Link {...props} replace />)`
  display: ${themeResponsiveProperty('flex','inline-flex')};
  align-items: ${themeResponsiveProperty(null,'flex-end')};
  white-space: initial;
  width: ${themeResponsiveProperty('15.5rem','10rem')};
  height: ${themeResponsiveProperty('12.7rem','11.5rem')};;
  padding: .5rem;

  ${props => props.theme.isMobile && css`
    background: linear-gradient(0, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%),
    transparent url(${({ cover }) => cover}) center;
  `};

  ${props => props.theme.isTablet && css`
    background: linear-gradient(0, rgba(0,0,0,.6) 10%, rgba(0,0,0,0) 50%),
    transparent url(${({ cover }) => cover}) center;
  `};

  background-size: cover;
  color: #fff;
  opacity: ${({ isActive }) => isActive ? 1 : .2};
  transition: opacity 300ms ease;

  &:hover {
    opacity: .8;
  }
`;

const CollectionCardTitle = styled.span`
  position: relative;
  line-height: 1.25rem;
  letter-spacing: ${themeResponsiveProperty('.1rem', null)};
  padding-right: ${themeResponsiveProperty('2rem', null)};

  ${props => props.theme.isTablet && css`
    &:after {
      content: "";
      position: absolute;
      top: -.25rem;
      left: 0;
      ${() => smallBorderStyle()}
    }
  `};
`;

const CollectionCardList = styled.div`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-scrolling: touch;
  transform: translateZ(0); // https://stackoverflow.com/a/30533339
`;

const CardContainer = styled.div `
  padding: 0 0.5rem;
`;
